import { useState } from 'react';
import { DSTextInput, DSTypography, DsSelect } from '@eiv2/design-system';

// Filtro de evento da área do ORGANIZADOR: os eventos autorizados vêm do
// `/api/organizer/access` — nunca um campo livre de organização. Quando a lista
// veio truncada (há mais eventos do que os listados), a consulta exige um
// evento, e o organizador pode informar o ID de um evento que não apareceu; quem
// confere se ele pode vê-lo é a API, a cada chamada.

export interface AuthorizedEvent {
  id: string;
  title: string | null;
}

/** Eventos do /access e se a lista é parcial. */
export interface OrganizerEventScope {
  events: readonly AuthorizedEvent[];
  eventsTruncated: boolean;
}

const EVENT_ID = /^\d{1,10}$/;

export function eventLabel(events: readonly AuthorizedEvent[], eventId: string): string {
  const title = events.find((event) => event.id === eventId)?.title;
  return title ? `${title} (${eventId})` : `Evento ${eventId}`;
}

/** Código de erro das APIs (`error` do corpo), quando houver. */
export function apiErrorCode(err: unknown): string | null {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

export interface OrganizerEventFilterProps {
  scope: OrganizerEventScope;
  /** Sem evento não há consulta (lista truncada ou 422 da API). */
  required: boolean;
  value: string;
  onChange: (eventId: string) => void;
}

export function OrganizerEventFilter({ scope, required, value, onChange }: OrganizerEventFilterProps) {
  const listed = scope.events.some((event) => event.id === value);
  const [manual, setManual] = useState(listed ? '' : value);
  const manualInvalid = manual.trim() !== '' && !EVENT_ID.test(manual.trim());

  const options = [
    ...(required ? [] : [{ value: '', label: 'Todos os eventos autorizados' }]),
    ...scope.events.map((event) => ({ value: event.id, label: eventLabel(scope.events, event.id) })),
  ];

  return (
    <div className="flex flex-col gap-3 md:col-span-2">
      <DsSelect
        onChange={(next) => {
          setManual('');
          onChange(typeof next === 'string' ? next : '');
        }}
        options={options}
        placeholder="Evento"
        value={listed ? value : ''}
      />
      {scope.eventsTruncated ? (
        <DSTextInput
          classNames={{ field: 'min-h-14 bg-light-gray' }}
          description="A lista mostra só parte dos seus eventos. Para outro evento, informe o ID; o acesso é conferido pelo servidor."
          errorMessage={manualInvalid ? 'Use apenas números.' : undefined}
          inputMode="numeric"
          label="Outro evento (ID)"
          onChange={(e) => {
            const text = e.target.value;
            setManual(text);
            const trimmed = text.trim();
            onChange(EVENT_ID.test(trimmed) ? trimmed : '');
          }}
          value={manual}
        />
      ) : null}
    </div>
  );
}

/** No lugar da lista quando a consulta exige evento e nenhum foi escolhido. */
export function EventRequiredState({ truncated }: { truncated: boolean }) {
  return (
    <div
      className="flex flex-col gap-1 rounded-3xl border border-gray-3 bg-body-light-mode-1 p-6 text-center"
      role="status"
    >
      <DSTypography as="p" className="text-[15px] font-semibold text-dark-3">
        Escolha um evento
      </DSTypography>
      <DSTypography as="p" className="text-[13px] font-normal text-gray-5">
        {truncated
          ? 'Você tem acesso a mais eventos do que conseguimos listar de uma vez. Escolha um evento no filtro para consultar.'
          : 'Há eventos demais para consultar de uma vez. Escolha um evento no filtro para continuar.'}
      </DSTypography>
    </div>
  );
}
